import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Download, Calculator, FileSpreadsheet, Loader2 } from "lucide-react";

export default function Comptabilite() {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [entreprises, setEntreprises] = useState([]);
  const [calc, setCalc] = useState({ entreprise_id: "", periode_debut: `${new Date().getFullYear()}-01-01`, periode_fin: `${new Date().getFullYear()}-12-31`, taux_charges: 0.42 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/entreprises").then(({ data }) => setEntreprises(data)); }, []);

  const exportFEC = async () => {
    setLoading(true);
    try {
      const resp = await api.get("/export/fec", { params: { annee }, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `FEC_OSCOPGE_${annee}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("FEC exporté");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const calculer = async () => {
    if (!calc.entreprise_id) { toast.error("Sélectionnez une entreprise"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/calculs/cout-reel", { ...calc, taux_charges: Number(calc.taux_charges) });
      setResult(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="comptabilite-page">
      <PageHeader
        title="Comptabilité & exports"
        subtitle="Export FEC (Fichier des Écritures Comptables) et calcul automatique du coût réel."
      />

      <Tabs defaultValue="fec">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="fec" data-testid="tab-fec">Export FEC</TabsTrigger>
          <TabsTrigger value="cout" data-testid="tab-cout">Calcul coût réel</TabsTrigger>
        </TabsList>

        <TabsContent value="fec" className="mt-5">
          <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-[#FDF9F1] border border-[#C5A059]/30 flex items-center justify-center">
                <FileSpreadsheet className="h-5 w-5 text-[#C5A059]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Fichier des Écritures Comptables</h3>
                <p className="text-xs text-slate-500">Norme française · format CSV pipe-séparé</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Année comptable</Label>
                <Input type="number" min="2020" max="2099" value={annee} onChange={(e)=>setAnnee(Number(e.target.value))} data-testid="fec-annee" className="mt-1" />
              </div>
              <Button onClick={exportFEC} disabled={loading} data-testid="fec-export-btn" className="w-full bg-[#C5A059] hover:bg-[#A88846] text-white">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" />Télécharger le FEC {annee}</>}
              </Button>
            </div>
            <div className="mt-4 text-xs text-slate-500">
              Inclut : appels à contribution émis (411/706) et frais directs (606/401).
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cout" className="mt-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-[#FDF9F1] border border-[#C5A059]/30 flex items-center justify-center">
                  <Calculator className="h-5 w-5 text-[#C5A059]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Calcul automatique</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Entreprise</Label>
                  <Select value={calc.entreprise_id} onValueChange={(v)=>setCalc({...calc, entreprise_id: v})}>
                    <SelectTrigger data-testid="cout-entreprise" className="mt-1"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{entreprises.map(e=><SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Période début</Label><Input type="date" value={calc.periode_debut} onChange={(e)=>setCalc({...calc, periode_debut: e.target.value})} className="mt-1" /></div>
                  <div><Label>Période fin</Label><Input type="date" value={calc.periode_fin} onChange={(e)=>setCalc({...calc, periode_fin: e.target.value})} className="mt-1" /></div>
                </div>
                <div><Label>Taux charges patronales</Label><Input type="number" step="0.01" min="0" max="1" value={calc.taux_charges} onChange={(e)=>setCalc({...calc, taux_charges: e.target.value})} className="mt-1" /></div>
                <Button onClick={calculer} disabled={loading} data-testid="cout-calc-btn" className="w-full bg-[#C5A059] hover:bg-[#A88846] text-white">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calculer"}
                </Button>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="cout-result">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Résultat</h3>
              {!result ? (
                <p className="text-sm text-slate-500">Lancez le calcul pour voir le détail.</p>
              ) : (
                <div className="space-y-2 text-sm">
                  {[
                    ["Heures validées", `${result.heures_validees} h`],
                    ["Tarif horaire moyen", `${result.tarif_moyen.toFixed(2)} €`],
                    ["Coût main d'œuvre", `${result.cout_main_oeuvre.toLocaleString("fr-FR")} €`],
                    ["Charges patronales", `${result.charges_patronales.toLocaleString("fr-FR")} €`],
                    ["Frais directs", `${result.total_frais.toLocaleString("fr-FR")} €`],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 py-2">
                      <span className="text-slate-600">{k}</span>
                      <span className="font-medium text-slate-900">{v}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-3 mt-2 bg-[#FDF9F1] -mx-2 px-2 rounded">
                    <span className="font-semibold text-slate-900">Coût total</span>
                    <span className="font-bold text-[#A88846] text-lg">{result.cout_total.toLocaleString("fr-FR")} €</span>
                  </div>
                  <div className="flex justify-between py-1 text-slate-600">
                    <span>Déjà émis (mensuel + régul)</span><span>{result.total_deja_emis.toLocaleString("fr-FR")} €</span>
                  </div>
                  <div className="flex justify-between py-3 bg-emerald-50 -mx-2 px-2 rounded">
                    <span className="font-semibold text-slate-900">Solde final dû</span>
                    <span className={`font-bold text-lg ${result.solde_final_du >= 0 ? "text-emerald-600" : "text-red-600"}`}>{result.solde_final_du.toLocaleString("fr-FR")} €</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">Basé sur {result.nb_timesheets} feuilles de temps validées.</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
